import { jsxs, jsx } from 'react/jsx-runtime';
import { useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { CircularProgress } from '../circular-progress/circular-progress.js';
import { Text } from '../text/text.js';
import { colors } from '../../tokens/colors/colors.js';
import '../../tokens/shadows/shadows.js';
import { spacing } from '../../tokens/spacing/spacing.js';
import '../../tokens/breakpoints/breakpoints.js';

/**
 * A PIN input component that renders a series of boxes for entering digits or characters.
 *
 * This component handles input selection, focus states, and limits the input length.
 * It uses a hidden input for accessibility and keyboard interactions while displaying
 * visual PIN slots to the user.
 *
 * @param value - The current value of the PIN input
 * @param onChange - Callback function triggered when the value changes
 * @param maxLength - Maximum length of the PIN input (default: 6)
 *
 */
function PinInput({ value, onChange, maxLength = 6, type = "number", onComplete, error, helperText, loading, }) {
    const [selection, setSelection] = useState(null);
    const inputRef = useRef(null);
    const prevInputRef = useRef(null);
    const valueIsValid = useCallback((value) => {
        if (type === "number") {
            return !isNaN(Number(value));
        }
        return true;
    }, [type]);
    function handleChange(event) {
        const value = event.target.value;
        if (!valueIsValid(value)) {
            event.preventDefault();
            return;
        }
        onChange(value);
    }
    function handleFocus() {
        const start = Math.min(value.length, maxLength - 1);
        const end = value.length;
        setSelection({
            start,
            end,
        });
    }
    function handleBlur() {
        setSelection(null);
    }
    /**
     * This effect is the core of the component, managing the selection behavior of the input.
     *
     * The mental model is the following:
     * - We use a hidden input to manage the selection and focus out of the box
     * - We listen to events on that hidden input to react in different ways
     * - For selection, we listen to `selectionchange` event on the document
     * - On selection change, we mirror the selection to the visible input
     * - The position of the caret/selection is based on indexes which are 0-based, i.e the positition of each character in the string
     */
    useEffect(() => {
        const input = inputRef.current;
        if (!input) {
            return;
        }
        /**
         * Initilize the previous selection state
         */
        prevInputRef.current = {
            start: inputRef.current.selectionStart,
            end: inputRef.current.selectionEnd,
            direction: inputRef.current.selectionDirection,
            value: inputRef.current.value,
        };
        /**
         * Handles document selection for the PIN input field.
         *
         * This function manages the selection behavior in the input element by:
         * - Implementing special selection logic based on caret position:
         *   - Selects first character when caret is at start position
         *   - Selects previous character when caret is at end position
         *   - Handles regular selection in middle positions respecting direction
         * - Updates internal selection state
         * - Stores selection for comparison in subsequent calls
         *
         * The function is designed to provide a more intuitive selection experience
         * for PIN input fields where each character position matters.
         *
         * This logic is highly inspired by: https://github.com/guilhermerodz/input-otp
         */
        function handleDocumentSelection() {
            if (!input) {
                return;
            }
            if (document.activeElement !== input) {
                return;
            }
            const selectionStart = input.selectionStart;
            const selectionEnd = input.selectionEnd;
            const selectionDirection = input.selectionDirection;
            const previousSelection = prevInputRef.current;
            const value = input.value;
            const maxLength = input.maxLength;
            if (selectionStart !== null &&
                selectionEnd !== null &&
                selectionDirection !== null &&
                previousSelection !== null &&
                previousSelection.start !== null &&
                previousSelection.end !== null) {
                /**
                 * Initilize the new start and end positions
                 * -1 means that we don't change the new selection
                 */
                let start = -1;
                let end = -1;
                let direction = "none";
                /**
                 * We only change the new selection if the value is not empty
                 */
                if (value.length !== 0) {
                    const hasReachedStart = selectionStart === 0;
                    const hasReachedEnd = selectionStart === maxLength;
                    // Check if the selection is a single caret
                    // This means that the no text is selected
                    const isSingleCaret = selectionStart === selectionEnd;
                    const isInsertingAtEnd = selectionStart === value.length && value.length < maxLength;
                    // We only handle selection when the there isn't a range of text selected
                    if (isSingleCaret && !isInsertingAtEnd) {
                        if (hasReachedStart) {
                            start = 0;
                            end = 1;
                            direction = "forward";
                        }
                        else if (hasReachedEnd) {
                            start = selectionStart - 1;
                            end = selectionStart;
                            direction = "backward";
                        }
                        else if (maxLength > 1 && value.length > 1) {
                            // This handles the case when the user selects a character in the middle
                            // 0 = forward
                            // -1 = backward
                            let directionOffset = 0;
                            direction =
                                selectionStart < previousSelection.end ? "backward" : "forward";
                            // Previous selection was in insert mode
                            // i.e there was no text in that position
                            const wasPreviouslyInsertMode = previousSelection.start === previousSelection.end &&
                                previousSelection.start < maxLength;
                            if (direction === "backward" && !wasPreviouslyInsertMode) {
                                directionOffset = -1;
                            }
                            start = directionOffset + selectionStart;
                            end = directionOffset + selectionStart + 1;
                        }
                        if (start !== -1 && end !== -1 && start !== end) {
                            input.setSelectionRange(start, end, direction);
                        }
                    }
                }
                // Update selection state
                const s = start !== -1 ? start : selectionStart;
                const e = end !== -1 ? end : selectionEnd;
                const d = direction ?? selectionDirection;
                setSelection({
                    start: s,
                    end: e,
                });
                // Store current selection for next comparison
                prevInputRef.current = {
                    start: s,
                    end: e,
                    direction: d,
                    value: value,
                };
            }
        }
        document.addEventListener("selectionchange", handleDocumentSelection);
        return () => {
            document.removeEventListener("selectionchange", handleDocumentSelection);
        };
    }, []);
    useEffect(() => {
        if (prevInputRef.current &&
            prevInputRef.current.value.length <= maxLength &&
            value.length === maxLength) {
            onComplete?.(value);
        }
    }, [value, maxLength]);
    /**
     * When the input gets disabled due to loading state, the focus is removed
     *
     * When we loading is set to false, we need to regain focus again
     */
    useEffect(() => {
        if (!loading) {
            inputRef.current?.focus();
        }
    }, [loading]);
    const pinInputsArray = new Array(maxLength).fill(null);
    return (jsxs("div", { children: [jsxs(Wrapper, { children: [jsx(SlotsWrapper, { "$disabled": !!loading, children: pinInputsArray.map((_, index) => (jsx(Slot, { "$selected": isSlotSelected({
                                selection,
                                index,
                            }), "$error": !!error, "$caret": !!selection &&
                                isSlotSelected({
                                    selection,
                                    index,
                                }) &&
                                (selection.end - selection.start === 1 ||
                                    selection.end - selection.start === 0), children: value?.[index] ?? "" }, index))) }), jsx(HiddenInput, { ref: inputRef, value: value, onChange: handleChange, onFocus: handleFocus, onBlur: handleBlur, maxLength: maxLength, disabled: loading, "aria-invalid": !!error }), jsx(LoadingWrapper, { hidden: !loading, children: jsx(CircularProgress, { size: 24 }) })] }), jsxs("div", { children: [error && typeof error === "string" && (jsx(Text, { variant: "bodyS", color: "emergency", textAlign: "left", children: error })), !!helperText && (jsx(Text, { variant: "bodyS", color: "secondary", textAlign: "left", children: helperText }))] })] }));
}
function isSlotSelected({ selection, index, }) {
    if (!selection) {
        return false;
    }
    const { start, end } = selection;
    if (start === end) {
        return index === start;
    }
    if (index >= start && index < end) {
        return true;
    }
    return false;
}
const Wrapper = styled.div `
  position: relative;
  display: inline-block;
`;
const SlotsWrapper = styled.div `
  display: flex;
  gap: ${spacing.M};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;
const Slot = styled.div `
  position: relative;
  width: calc(1ch + (${spacing.M} * 2));
  height: calc(1lh + (${spacing.M} * 2));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(1rem, 2vw + 0.5rem, 1.5rem);
  line-height: 1.4;

  background-color: ${colors.neutral};
  border-radius: 8px;
  border-width: 1px;
  border-style: solid;
  border-color: ${({ $selected, $error }) => {
    if ($selected) {
        return colors.primary;
    }
    if ($error) {
        return colors.systemError;
    }
    return colors.neutral;
}};
  &::after {
    content: "";
    flex-shrink: 0;
    flex-grow: 0;
    width: 1px;
    height: 1lh;
    background-color: ${colors.textPrimary};
    visibility: hidden;
    ${({ $caret }) => $caret && "animation: pulsate 1s steps(1, start)  infinite;"}
  }

  @keyframes pulsate {
    0% {
      visibility: visible;
    }
    50% {
      visibility: hidden;
    }
    100% {
      visibility: visible;
    }
  }
`;
const LoadingWrapper = styled.div `
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
const HiddenInput = styled.input `
  position: absolute;
  inset: 0;
  opacity: 1; // Required on IOS
  background-color: transparent;
  color: transparent;
  outline: none;
  border: none;
  caret-color: transparent;
  pointer-events: all;
  letter-spacing: -5rem;
`;

export { PinInput };
